

import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, VideoOff, Mic, MicOff, Users, Send, MessageCircle, 
  Settings, Maximize2, Volume2, VolumeX, Heart, Smile, 
  Camera, Monitor, Phone, PhoneOff, Plus, X, Lock, 
  Unlock, Crown, Eye, EyeOff, Share2, Sparkles, Zap,
  TrendingUp, Clock, Star, Award, Gift, Leaf, Filter,
  Search, Hash, Globe, AlertCircle, CheckCircle
} from 'lucide-react'; 

export const RemoteVideo = ({ userId, stream, onReconnectRequest }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);  

  useEffect(() => {
    console.log(`🎬 RemoteVideo effect for ${userId}:`, {
      hasStream: !!stream,
      hasVideoRef: !!videoRef.current,
      streamId: stream?.id,
      trackCount: stream?.getTracks().length,
      currentSrc: videoRef.current?.srcObject?.id
    });
    
    if (videoRef.current && stream) {
      console.log(`📺 Setting srcObject for ${userId}`);
      videoRef.current.srcObject = stream;
      videoRef.current.muted = false;
      setIsPlaying(false);
      
      // IMMEDIATE play attempt
      videoRef.current.play()
        .then(() => {
          console.log(`✅ AUTOPLAY SUCCESS for ${userId}`);
          setIsPlaying(true);
        })
        .catch(e => {
          console.log(`❌ Autoplay failed for ${userId}, manual click needed`);
          setIsPlaying(false);
        });
    }
  }, [stream, userId]);

  const handleVideoClick = async () => {
    console.log(`🔄 ENHANCED RECONNECT for ${userId}`);
    setIsReconnecting(true);
    
    try {
      // First: Try basic play
      if (videoRef.current && stream) {
        videoRef.current.muted = false;
        try {
          await videoRef.current.play();
          setIsPlaying(true);
          setReconnectAttempts(0);
          setIsReconnecting(false);
          return;
        } catch (e) {
          console.log(`Basic play failed, requesting reconnection`);
        }
      }

      // Second: Request parent to reconnect WebRTC
      if (onReconnectRequest && reconnectAttempts < 3) {
        setReconnectAttempts(prev => prev + 1);
        await onReconnectRequest(userId);
        
        // Wait a bit then try play again
        setTimeout(async () => {
          if (videoRef.current) {
            try {
              await videoRef.current.play();
              setIsPlaying(true);
            } catch (e) {
              console.error(`Final play attempt failed for ${userId}`);
            }
            setIsReconnecting(false);
          }
        }, 2000);
      } else {
        setIsReconnecting(false);
      }
    } catch (error) {
      console.error(`Reconnection failed for ${userId}:`, error);
      setIsReconnecting(false);
    }
  };

  return (
    <div style={{
      position: 'relative',
      background: '#000',
      borderRadius: '8px',
      overflow: 'hidden',
      aspectRatio: '16/9'
    }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          backgroundColor: '#000',
          cursor: 'pointer'
        }}
        onClick={handleVideoClick}
        onCanPlay={() => {
          console.log(`🎬 Video can play for ${userId}`);
          if (videoRef.current && !isPlaying) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          }
        }}
        onLoadedMetadata={() => {
          console.log(`✅ Video metadata loaded for ${userId}`);
          if (videoRef.current && !isPlaying) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          }
        }}
        onPlaying={() => {
          console.log(`▶️ Video PLAYING for ${userId}`);
          setIsPlaying(true);
        }}
        onError={(e) => console.error(`❌ Video error for ${userId}:`, e)}
      />
      
      {/* Status indicator */}
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        padding: '4px 8px',
        background: isPlaying ? 'rgba(76, 175, 80, 0.8)' : 'rgba(244, 67, 54, 0.8)',
        borderRadius: '4px',
        fontSize: '10px',
        color: 'white',
        fontWeight: '600'
      }}>
        {isPlaying ? '🟢 LIVE' : '⏸️ Click to Play'}
      </div>
      
      {/* User name tag */}
      <div style={{
        position: 'absolute',
        bottom: '8px',
        left: '8px',
        background: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        User {userId.slice(-4)}
      </div>
      
      {/* Play button overlay */}
      {!isPlaying && stream && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)'
        }}>
          <button 
            onClick={handleVideoClick}
            disabled={isReconnecting}
            style={{
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              background: isReconnecting ? '#ff9800' : '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isReconnecting ? 'wait' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isReconnecting ? 0.7 : 1
            }}
          >
            {isReconnecting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Reconnecting... ({reconnectAttempts}/3)
              </>
            ) : (
              <>
                <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Reconnect Stream
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};